import {
  getFeatureFlagsControllerGetFlagsQueryKey,
  getImportsControllerGetImportQueryKey,
  getImportsControllerListImportsQueryKey,
} from '@artic/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { partnerDetailQueryOptions } from '../partners/queries';
import { useRealtimeSocket } from './realtime-provider';
import type { ImportsChangedMessage } from './types';

interface UsePartnerRealtimeSyncOptions {
  /** Feature-flag gate. When false the hook no-ops and opens no socket. */
  enabled: boolean;
}

/**
 * Joins `partner:<partnerId>` once and listens for partner-scoped refetch
 * signals:
 *
 *  - `partner_changed` refetches feature flags (so the sidebar nav updates
 *    live as flags flip) and the admin partner-detail record.
 *  - `imports_changed` refetches import lists/details as upload or Drive import
 *    rows are inserted and progress through statuses.
 *
 * No presence. Shares the ref-counted socket with `useProjectChannel` via the
 * `RealtimeProvider`.
 */
export function usePartnerRealtimeSync(
  partnerId: string | undefined,
  { enabled }: UsePartnerRealtimeSyncOptions,
): void {
  const queryClient = useQueryClient();
  const { acquireSocket, releaseSocket } = useRealtimeSocket();

  useEffect(() => {
    if (!enabled || !partnerId) return;

    const socket = acquireSocket();
    if (!socket) return;

    const chan = socket.channel(`partner:${partnerId}`, {});
    const partnerRef = chan.on('partner_changed', () => {
      // Nav flags — the exact query key the FeatureFlagProvider's suspense query
      // reads, so this is a background refetch (no suspense-fallback flash).
      void queryClient.invalidateQueries({
        queryKey: getFeatureFlagsControllerGetFlagsQueryKey({ partner_id: partnerId }),
      });
      // Admin partner-detail record, refreshed if the detail page is open.
      void queryClient.invalidateQueries({
        queryKey: partnerDetailQueryOptions(partnerId).queryKey,
      });
    });

    const importsRef = chan.on('imports_changed', (msg: ImportsChangedMessage) => {
      void queryClient.invalidateQueries({
        queryKey: getImportsControllerListImportsQueryKey(),
      });
      void queryClient.invalidateQueries({
        queryKey: getImportsControllerGetImportQueryKey(msg.import_id),
      });
    });

    chan.join();

    return () => {
      chan.off('partner_changed', partnerRef);
      chan.off('imports_changed', importsRef);
      chan.leave();
      releaseSocket();
    };
  }, [enabled, partnerId, acquireSocket, releaseSocket, queryClient]);
}
